<template>
  <div data-slot="stripe-card-input" class="relative">
    <!-- The card element is a single-line iframe: give it the kit input frame. -->
    <div
      ref="stripeMount"
      :class="!clientSecret && 'glass-input h-9 rounded-md px-3 pt-2 focus-within:ring-1 focus-within:ring-ring'"
    />

    <div v-if="isLoading || !stripeReady" class="absolute inset-0 flex items-center bg-background/60 pl-3">
      <loader-circle-icon v-if="!clientSecret" class="size-5 animate-spin text-primary" />
    </div>
  </div>
</template>

<script>
import { LoaderCircle } from '@lucide/vue';
import { loadStripe } from '@stripe/stripe-js';

import CompanyPaymentService from '@/services/CompanyPayment';

export default {
  name: 'StripeCardInput',
  components: {
    LoaderCircleIcon: LoaderCircle,
  },
  props: {
    clientSecret: {
      type: String,
      required: false,
      default: () => null,
    },
    forceLoading: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  data() {
    return {
      isCompleted: false,
      stripeElementIsLoading: false,
      stripeError: null,
      stripeReady: false,
    };
  },
  computed: {
    publishableKey() {
      return process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY;
    },
    isLoading() {
      return this.stripeElementIsLoading || this.forceLoading;
    },
  },
  watch: {
    stripeElementIsLoading(val) {
      if (val && this.stripeElement) {
        this.stripeElement.blur();
      }
    },
  },
  async mounted() {
    await this.initStripe();
  },
  beforeUnmount() {
    if (this.stripeElement) {
      this.stripeElement.destroy();
    }
  },
  methods: {
    async initStripe() {
      try {
        this.stripe = await loadStripe(this.publishableKey, {
          locale: this.$store.getters['app/language'],
        });

        if (!this.stripe) {
          throw new Error('Stripe.js failed to load');
        }

        this.stripeElements = this.stripe.elements(this.clientSecret ? { clientSecret: this.clientSecret } : {});

        if (this.clientSecret) {
          this.stripeElement = this.stripeElements.create('payment', {
            fields: {
              billingDetails: {
                address: {
                  country: 'never',
                  postalCode: 'never',
                },
              },
            },
          });
        }
        else {
          this.stripeElement = this.stripeElements.create('card', {
            iconStyle: 'solid',
            hidePostalCode: true,
          });
        }

        this.stripeElement.on('change', val => this.onElementChange(val));
        this.stripeElement.on('ready', () => this.$emit('ready'));
        this.stripeElement.mount(this.$refs.stripeMount);

        this.stripeReady = true;
      }
      catch (err) {
        this.stripeError = err;
        this.$emit('error', err);
      }
    },
    async save() {
      if (this.isLoading) {
        return false;
      }

      this.stripeError = null;

      if (this.clientSecret) {
        return this.confirmPayment();
      }

      return this.confirmCard();
    },
    onUpdateIsLoading(val) {
      if (val) {
        this.stripeElementIsLoading = true;
      }
      else {
        this.$nextTick(() => {
          if (this.stripeError !== null) {
            this.$emit('error', this.stripeError);
          }
          else {
            this.$emit('success');
          }

          this.stripeElementIsLoading = false;
        });
      }
    },
    async confirmPayment() {
      this.onUpdateIsLoading(true);

      let success = false;
      try {
        const { error } = await this.stripe.confirmPayment({
          elements: this.stripeElements,
          redirect: 'if_required',
          confirmParams: {
            payment_method_data: {
              billing_details: {
                address: {
                  country: this.$root.currentCompany.billing_country,
                  postal_code: this.$root.currentCompany.billing_zip,
                },
              },
            },
          },
        });

        if (error) {
          throw error;
        }

        success = true;
      }
      catch (err) {
        this.stripeError = err;
      }

      this.onUpdateIsLoading(false);

      return success;
    },
    async confirmCard() {
      this.onUpdateIsLoading(true);

      let success = false;
      try {
        const resp = await CompanyPaymentService.createCardConfirmSetupIntent(this.$root.currentCompany.id);

        const { setupIntent, error } = await this.stripe.confirmCardSetup(resp.client_secret, {
          payment_method: {
            card: this.stripeElement,
          },
        });

        if (error) {
          throw error;
        }

        await CompanyPaymentService.setPaymentMethodAsDefault(this.$root.currentCompany.id, setupIntent.payment_method);

        success = true;
      }
      catch (err) {
        this.stripeError = err;
      }

      this.onUpdateIsLoading(false);

      return success;
    },
    onElementChange(val) {
      this.isCompleted = val.complete;

      if (val.error) {
        this.stripeError = val.error.message;
      }
      else {
        this.stripeError = null;
      }
    },
  },
};
</script>
